import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal, Space, Upload } from 'antd';
import { LoadingOutlined, UploadOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { mimeToExt, downloadObjectUrl } from './imageUtils';

interface IdPhotoUploaderProps {
  label: string;
  /** 本次弹窗里新选的、还没提交的文件 */
  stagedFile: File | null;
  onStagedFileChange: (file: File | null) => void;
  /** 已保存在服务器上的照片下载地址（鉴权接口），没有照片时为空 */
  existingUrl?: string | null;
  /** 拉取已保存照片的字节内容（用于缩略图和查看大图，两处共用一次请求结果） */
  fetchBlob: () => Promise<Blob>;
  /** 立即删除服务器上已保存的照片 */
  deleteExisting: () => Promise<void>;
}

/**
 * 证件照片上传控件：缩略图 + 右侧固定"查看"/"删除"按钮。
 * 点击缩略图直接重新选择文件替换；"删除"在有待提交的新选文件时只是取消这次选择
 * （还原成底下已保存的照片，或者空），只有在没有待提交新文件、真的要删掉已保存照片时
 * 才会调用 deleteExisting 立即生效——这样不需要单独的"取消选择"按钮。
 */
const IdPhotoUploader: React.FC<IdPhotoUploaderProps> = ({
  label,
  stagedFile,
  onStagedFileChange,
  existingUrl,
  fetchBlob,
  deleteExisting,
}) => {
  const [stagedPreviewUrl, setStagedPreviewUrl] = useState<string | null>(null);
  const [existingBlobUrl, setExistingBlobUrl] = useState<string | null>(null);
  const [existingBlobType, setExistingBlobType] = useState<string | null>(null);
  const [existingLoading, setExistingLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const existingUrlRef = useRef<string | null | undefined>(undefined);

  // 新选了文件：生成本地预览，卸载/换文件时回收
  useEffect(() => {
    if (!stagedFile) {
      setStagedPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(stagedFile);
    setStagedPreviewUrl(url);
    return () => { URL.revokeObjectURL(url); };
  }, [stagedFile]);

  // 已保存照片的地址变了（换了一条记录 / 照片被删除 / 重新上传成功），清掉旧缓存
  useEffect(() => {
    if (existingUrl !== existingUrlRef.current) {
      existingUrlRef.current = existingUrl;
      setExistingBlobUrl((prev) => {
        if (prev) { try { URL.revokeObjectURL(prev); } catch {} }
        return null;
      });
    }
  }, [existingUrl]);

  // 缩略图/查看大图共用同一次已保存照片的拉取结果，只在真的需要展示时才拉一次
  useEffect(() => {
    if (!existingUrl || stagedFile || existingBlobUrl) return;
    let cancelled = false;
    setExistingLoading(true);
    fetchBlob()
      .then((blob) => {
        if (cancelled) return;
        setExistingBlobUrl(URL.createObjectURL(blob));
        setExistingBlobType(blob.type);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setExistingLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingUrl, stagedFile, existingBlobUrl]);

  const previewSrc = stagedPreviewUrl || existingBlobUrl;
  const showThumb = !!previewSrc || existingLoading;

  const uploadProps: UploadProps = {
    multiple: false,
    showUploadList: false,
    accept: 'image/*',
    beforeUpload: (file) => { onStagedFileChange(file as File); return false; },
  };

  const handleDelete = async () => {
    if (stagedFile) {
      // 只是取消这次还没提交的新选择，还原成底下已保存的照片（如果有的话）
      onStagedFileChange(null);
      return;
    }
    if (!existingUrl) return;
    setDeleting(true);
    try {
      await deleteExisting();
      setExistingBlobUrl((prev) => {
        if (prev) { try { URL.revokeObjectURL(prev); } catch {} }
        return null;
      });
    } catch {
      // 父组件已经弹过错误提示，这里只是防止未处理的 rejection 冒泡到控制台
    } finally {
      setDeleting(false);
    }
  };

  const handleDownload = () => {
    if (!previewSrc) return;
    const filename = stagedFile
      ? stagedFile.name
      : `${label.replace(/（可选）/g, '').trim()}.${mimeToExt(existingBlobType)}`;
    downloadObjectUrl(previewSrc, filename);
  };

  return (
    <div>
      <div style={{ marginBottom: 4, color: 'rgba(0, 0, 0, 0.88)' }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Upload {...uploadProps}>
          {showThumb ? (
            <div
              style={{
                width: 64, height: 64, border: '1px solid #d9d9d9', borderRadius: 4,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: '#fafafa', cursor: 'pointer', overflow: 'hidden',
              }}
            >
              {previewSrc ? (
                <img src={previewSrc} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <LoadingOutlined />
              )}
            </div>
          ) : (
            <Button icon={<UploadOutlined />}>选择文件</Button>
          )}
        </Upload>
        {previewSrc && (
          <Space direction="vertical" size={4}>
            <Button size="small" onClick={() => setPreviewOpen(true)}>查看</Button>
            <Button size="small" danger loading={deleting} onClick={handleDelete}>删除</Button>
          </Space>
        )}
      </div>
      <Modal
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        title={label}
        centered
        footer={[
          <Button key="download" onClick={handleDownload}>下载</Button>,
          <Button key="close" onClick={() => setPreviewOpen(false)}>关闭</Button>,
        ]}
      >
        {previewSrc && <img src={previewSrc} alt={label} style={{ width: '100%' }} />}
      </Modal>
    </div>
  );
};

export default IdPhotoUploader;
